import { useEffect, useMemo, useState } from "react";
import type { AccountingHealth } from "@/api/accounting";
import { exchangePlaidPublicToken, syncPlaidAccount } from "@/api/bank";
import { Button } from "@/components/ui/button";
import { MappingHealthBanner } from "@/components/accounting/MappingHealthBanner";
import { MappingHealthModal } from "@/components/accounting/MappingHealthModal";
import { useToast } from "@/components/ToastProvider";
import { useLedger } from "@/hooks/useLedger";
import { useAccountingHealth } from "@/hooks/useAccountingHealth";
import { useBanking } from "@/hooks/useBanking";
import type { BankTransaction } from "@/types/bank";
import { useUnifiedErrorSurface } from "@/hooks/useUnifiedErrorSurface";
import { openPlaidLink, type PlaidOnSuccessMetadata } from "@/lib/plaid";
import { SectionError, SkeletonRows } from "@/components/ui/error-ui";

const currency = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

type OffsetLine = {
  accountId: number | "";
  debit: string;
  credit: string;
  memo: string;
};

type SplitSegment = {
  amount: string;
  memo: string;
  lines: OffsetLine[];
};

const defaultLine = (): OffsetLine => ({ accountId: "", debit: "", credit: "", memo: "" });
const defaultSegment = (): SplitSegment => ({ amount: "", memo: "", lines: [defaultLine()] });

type BankReconcilePanelProps = {
  health?: AccountingHealth | null;
  showHealthBanner?: boolean;
  healthLoading?: boolean;
  healthError?: string | null;
};

export function BankReconcilePanel({
  health: externalHealth,
  healthLoading,
  healthError,
  showHealthBanner = true,
}: BankReconcilePanelProps = {}) {
  const { push } = useToast();
  const {
    accounts,
    transactions,
    rules,
    savingRule,
    selectedAccountId,
    setSelectedAccountId,
    loading,
    error,
    importing,
    savingAccount,
    reconcilingId,
    refresh,
    addAccount,
    importStatementFile,
    reconcileTransaction,
    matchTransaction,
    splitTransaction,
    addRule,
  } = useBanking();
  const { accounts: ledgerAccounts, journals, loading: ledgerLoading } = useLedger();

  const [newAccount, setNewAccount] = useState({
    name: "Operating Checking",
    institution: "",
    account_number_last4: "",
    currency: "USD",
    ledger_account_id: 0,
  });
  const [newRule, setNewRule] = useState({ pattern: "Shopify", account_id: 0, memo_template: "Shopify sale" });
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [selectedTxnId, setSelectedTxnId] = useState<string | null>(null);
  const [selectedJournalId, setSelectedJournalId] = useState<number | null>(null);
  const [offsetLines, setOffsetLines] = useState<OffsetLine[]>([defaultLine()]);
  const [memo, setMemo] = useState("");
  const [matchEntryId, setMatchEntryId] = useState("");
  const [splitMode, setSplitMode] = useState(false);
  const [splitSegments, setSplitSegments] = useState<SplitSegment[]>([defaultSegment(), defaultSegment()]);
  const [plaidLedgerAccountId, setPlaidLedgerAccountId] = useState<number | null>(null);
  const [plaidConnecting, setPlaidConnecting] = useState(false);
  const [syncingAccountId, setSyncingAccountId] = useState<number | null>(null);
  const [healthModalOpen, setHealthModalOpen] = useState(false);
  const {
    health: internalHealth,
    loading: internalLoading,
    error: internalError,
    refresh: refreshHealth,
  } = useAccountingHealth(!externalHealth);
  const mergedHealth = externalHealth ?? internalHealth;
  const bannerLoading = healthLoading ?? internalLoading;
  const bannerError = healthError ?? internalError;
  const { notifyError } = useUnifiedErrorSurface();

  const activeTxn: BankTransaction | null = useMemo(() => {
    if (transactions.length === 0) return null;
    return transactions.find((txn) => txn.id === selectedTxnId) ?? transactions[0];
  }, [transactions, selectedTxnId]);

  const plaidBankAccounts = useMemo(
    () => accounts.filter((account) => account.connection_provider === "plaid" && (account.channel ?? "bank") !== "venmo"),
    [accounts],
  );

  useEffect(() => {
    if (!journals.length) return;
    setSelectedJournalId((prev) => prev ?? journals.find((journal) => journal.code === "BNK")?.id ?? journals[0].id);
  }, [journals]);

  useEffect(() => {
    if (!activeTxn) return;
    setSelectedTxnId(activeTxn.id);
    const baseAmount = Math.abs(Number(activeTxn.amount_signed) || 0).toFixed(2);
    const isDeposit = Number(activeTxn.amount_signed) > 0;
    setOffsetLines([
      {
        accountId: activeTxn.suggested_account_id ?? "",
        debit: isDeposit ? "" : baseAmount,
        credit: isDeposit ? baseAmount : "",
        memo: activeTxn.suggested_memo ?? activeTxn.description,
      },
    ]);
    setMemo(activeTxn.description);
    setSplitSegments([
      {
        amount: baseAmount,
        memo: activeTxn.description,
        lines: [
          {
            accountId: activeTxn.suggested_account_id ?? "",
            debit: isDeposit ? "" : baseAmount,
            credit: isDeposit ? baseAmount : "",
            memo: activeTxn.suggested_memo ?? activeTxn.description,
          },
        ],
      },
      defaultSegment(),
    ]);
  }, [activeTxn?.id]);

  useEffect(() => {
    setNewAccount((prev) => ({
      ...prev,
      ledger_account_id:
        prev.ledger_account_id ||
        ledgerAccounts.find((acct) => acct.code?.startsWith("10"))?.id ||
        ledgerAccounts[0]?.id ||
        0,
    }));
    setNewRule((prev) => ({
      ...prev,
      account_id: prev.account_id || ledgerAccounts.find((acct) => acct.code?.startsWith("40"))?.id || ledgerAccounts[0]?.id || 0,
    }));
    setPlaidLedgerAccountId((prev) => {
      if (prev) return prev;
      return ledgerAccounts.find((acct) => acct.code?.startsWith("10"))?.id ?? ledgerAccounts[0]?.id ?? null;
    });
  }, [ledgerAccounts]);

  const handleAddOffsetLine = () => setOffsetLines((prev) => [...prev, defaultLine()]);
  const handleRemoveOffsetLine = (index: number) => setOffsetLines((prev) => (prev.length <= 1 ? prev : prev.filter((_, idx) => idx !== index)));
  const handleUpdateLine = (index: number, patch: Partial<OffsetLine>) => {
    setOffsetLines((prev) => prev.map((line, idx) => (idx === index ? { ...line, ...patch } : line)));
  };
  const handleUpdateSplitLine = (segmentIndex: number, lineIndex: number, patch: Partial<OffsetLine>) => {
    setSplitSegments((prev) =>
      prev.map((segment, idx) => {
        if (idx !== segmentIndex) return segment;
        const lines = segment.lines.map((line, lidx) => (lidx === lineIndex ? { ...line, ...patch } : line));
        return { ...segment, lines };
      }),
    );
  };
  const addSplitLine = (segmentIndex: number) => {
    setSplitSegments((prev) =>
      prev.map((segment, idx) => (idx === segmentIndex ? { ...segment, lines: [...segment.lines, defaultLine()] } : segment)),
    );
  };
  const removeSplitLine = (segmentIndex: number, lineIndex: number) => {
    setSplitSegments((prev) =>
      prev.map((segment, idx) => {
        if (idx !== segmentIndex) return segment;
        if (segment.lines.length <= 1) return segment;
        return { ...segment, lines: segment.lines.filter((_, lidx) => lidx !== lineIndex) };
      }),
    );
  };
  const addSplitSegment = () => setSplitSegments((prev) => [...prev, defaultSegment()]);
  const removeSplitSegment = (index: number) => setSplitSegments((prev) => (prev.length <= 2 ? prev : prev.filter((_, idx) => idx !== index)));

  const handleCreateAccount = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!newAccount.ledger_account_id) {
      push({ title: "Missing ledger account", message: "Select a ledger account for the bank asset." });
      return;
    }
    try {
      await addAccount(newAccount);
      push({ title: "Bank account created", message: newAccount.name });
    } catch (err) {
      notifyError(err);
    }
  };

  const handleAddRule = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!newRule.account_id) {
      push({ title: "Missing ledger account", message: "Select a ledger account for the rule." });
      return;
    }
    await addRule(newRule);
    push({ title: "Rule saved", message: newRule.pattern });
  };

  const handleImport = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedAccountId || !uploadFile) {
      push({ title: "Select account", message: "Choose a bank account and CSV before importing." });
      return;
    }
    await importStatementFile(selectedAccountId, uploadFile);
    push({ title: "Statement imported", message: uploadFile.name });
    setUploadFile(null);
  };

  const handlePlaidConnect = async () => {
    if (!plaidLedgerAccountId) {
      push({ title: "Select ledger account", message: "Choose which ledger account should mirror the Plaid feed." });
      return;
    }
    setPlaidConnecting(true);
    try {
      await openPlaidLink({
        channel: "bank",
        onExit: () => setPlaidConnecting(false),
        onSuccess: async (publicToken: string, metadata: PlaidOnSuccessMetadata) => {
          const account = metadata.accounts?.[0];
          if (!account) {
            push({ title: "No account selected", message: "Select an account inside Plaid Link before continuing." });
            setPlaidConnecting(false);
            return;
          }
          try {
            await exchangePlaidPublicToken({
              public_token: publicToken,
              account_id: account.id,
              account_name: account.name,
              account_mask: account.mask,
              institution_id: metadata.institution?.institution_id,
              institution_name: metadata.institution?.name,
              ledger_account_id: plaidLedgerAccountId,
              channel: "bank",
            });
            push({ title: "Plaid connected", message: account.name ?? "Bank feed linked" });
            await refresh();
          } catch (err) {
            notifyError(err);
          } finally {
            setPlaidConnecting(false);
          }
        },
      });
    } catch (err) {
      setPlaidConnecting(false);
      notifyError(err);
    }
  };

  const handlePlaidSync = async (accountId: number) => {
    setSyncingAccountId(accountId);
    try {
      const result = await syncPlaidAccount(accountId);
      push({
        title: "Sync complete",
        message: `Imported ${result.imported} new transactions (${result.updated} updated).`,
      });
      await refresh();
    } catch (err) {
      notifyError(err);
    } finally {
      setSyncingAccountId(null);
    }
  };

  const preparedLines = () => {
    return offsetLines
      .map((line) => ({
        account_id: line.accountId || undefined,
        debit: line.debit ? Number(line.debit) : undefined,
        credit: line.credit ? Number(line.credit) : undefined,
        memo: line.memo || undefined,
      }))
      .filter((line) => line.account_id && (line.debit || line.credit));
  };

  const formatLastSynced = (value?: string | null) => {
    if (!value) return "Never synced";
    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  };

  const handleMatch = async () => {
    if (!activeTxn) return;
    if (!matchEntryId) {
      push({ title: "Specify entry", message: "Provide a ledger entry ID." });
      return;
    }
    try {
      await matchTransaction(activeTxn.id, matchEntryId);
      push({ title: "Matched", message: activeTxn.description });
    } catch (err) {
      notifyError(err);
    }
  };

  const handleOffset = async () => {
    if (!activeTxn) return;
    const lines = preparedLines();
    if (!lines.length) {
      push({ title: "Missing lines", message: "Add at least one entry line." });
      return;
    }
    if (!selectedJournalId) {
      push({ title: "Select journal", message: "Choose a ledger journal first." });
      return;
    }
    try {
      await reconcileTransaction({
        transaction_id: activeTxn.id,
        memo,
        lines,
        journal_id: selectedJournalId,
      });
      push({ title: "Reconciled", message: activeTxn.description });
    } catch (err) {
      notifyError(err);
    }
  };

  const handleSplit = async () => {
    if (!activeTxn) return;
    const segmentsPayload = splitSegments
      .map((segment) => ({
        amount: segment.amount ? Number(segment.amount) : null,
        memo: segment.memo || undefined,
        lines: segment.lines
          .map((line) => ({
            account_id: line.accountId || undefined,
            debit: line.debit ? Number(line.debit) : undefined,
            credit: line.credit ? Number(line.credit) : undefined,
            memo: line.memo || undefined,
          }))
          .filter((line) => line.account_id && (line.debit || line.credit)),
      }))
      .filter((segment) => Boolean(segment.amount && segment.lines.length));
    if (!segmentsPayload.length) {
      push({ title: "Missing split", message: "Add at least one split segment." });
      return;
    }
    if (!selectedJournalId) {
      push({ title: "Select journal", message: "Choose a ledger journal first." });
      return;
    }
    try {
      await splitTransaction({
        transaction_id: activeTxn.id,
        journal_id: selectedJournalId,
        splits: segmentsPayload.map((segment) => ({
          amount: segment.amount as number,
          memo: segment.memo,
          lines: segment.lines,
        })),
      });
      push({ title: "Split recorded", message: activeTxn.description });
    } catch (err) {
      notifyError(err);
    }
  };

  return (
    <div className="space-y-6 rounded-2xl border border-border bg-background/70 p-6">
      {showHealthBanner && (
        <MappingHealthBanner
          health={mergedHealth}
          loading={bannerLoading}
          error={bannerError}
          onViewRequirements={() => setHealthModalOpen(true)}
        />
      )}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-primary-foreground dark:text-amber-100">Bank reconciliation</h2>
          <p className="text-sm text-muted-foreground">Import CSVs, auto-match transactions, and reconcile to ledger.</p>
        </div>
        <Button variant="outline" onClick={() => refresh()} disabled={loading}>
          {loading ? "Refreshing…" : "Refresh"}
        </Button>
      </div>
      {error && <SectionError message={error} onRetry={() => void refresh()} />}
      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <section className="space-y-4 rounded-2xl border border-border bg-background/70 p-5">
          <header className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-primary-foreground dark:text-amber-200">Transactions</h3>
              <p className="text-xs text-muted-foreground">Select a bank account to review imported lines.</p>
            </div>
            <select
              className="rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
              value={selectedAccountId ?? ""}
              onChange={(event) => setSelectedAccountId(event.target.value || null)}
            >
              <option value="" disabled>
                Select account
              </option>
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name}
                </option>
              ))}
            </select>
          </header>
          <div className="custom-scrollbar max-h-80 space-y-2 overflow-y-auto pr-2">
            {loading && transactions.length === 0 && <SkeletonRows rows={4} />}
            {transactions.map((txn) => (
              <button
                key={txn.id}
                type="button"
                onClick={() => setSelectedTxnId(txn.id)}
                className={`w-full rounded-xl border px-3 py-3 text-left text-sm ${
                  activeTxn?.id === txn.id ? "border-primary/60 bg-primary/10 text-primary" : "border-border bg-background/30 text-foreground/80"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold">{txn.description}</span>
                  <span className="text-primary-foreground dark:text-amber-100">{currency.format(Number(txn.amount_signed))}</span>
                </div>
                <p className="text-xs text-muted-foreground">{txn.posted_at ? new Date(txn.posted_at).toLocaleDateString() : "Pending"}</p>
              </button>
            ))}
            {transactions.length === 0 && <p className="text-xs text-muted-foreground">No transactions imported yet.</p>}
          </div>
        </section>
        <section className="space-y-4 rounded-2xl border border-border bg-background/70 p-5">
          <header>
            <h3 className="text-lg font-semibold text-primary-foreground dark:text-amber-200">Import / accounts</h3>
            <p className="text-xs text-muted-foreground">Add bank feeds and upload CSV statements.</p>
          </header>
          <form onSubmit={handleCreateAccount} className="space-y-2 text-xs">
            <p className="font-semibold text-foreground/70">Add account</p>
            <div className="grid gap-2 md:grid-cols-2">
              <input
                className="rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
                placeholder="Account name"
                value={newAccount.name}
                onChange={(event) => setNewAccount((prev) => ({ ...prev, name: event.target.value }))}
              />
              <input
                className="rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
                placeholder="Institution"
                value={newAccount.institution}
                onChange={(event) => setNewAccount((prev) => ({ ...prev, institution: event.target.value }))}
              />
              <input
                className="rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
                placeholder="Last 4"
                value={newAccount.account_number_last4}
                onChange={(event) => setNewAccount((prev) => ({ ...prev, account_number_last4: event.target.value }))}
              />
              <select
                className="rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
                value={newAccount.ledger_account_id}
                onChange={(event) => setNewAccount((prev) => ({ ...prev, ledger_account_id: Number(event.target.value) }))}
              >
                {ledgerAccounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.code} · {account.name}
                  </option>
                ))}
              </select>
            </div>
            <Button type="submit" size="sm" disabled={savingAccount || ledgerLoading}>
              {savingAccount ? "Saving…" : "Save account"}
            </Button>
          </form>
          <section className="space-y-2 rounded-2xl border border-border bg-background/60 p-4 text-xs">
            <p className="font-semibold text-foreground/70">Link with Plaid</p>
            <p className="text-muted-foreground">
              Launch Plaid Link to connect live bank feeds and keep transactions in sync automatically.
            </p>
            <select
              className="rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
              value={plaidLedgerAccountId ?? ""}
              onChange={(event) => setPlaidLedgerAccountId(event.target.value ? Number(event.target.value) : null)}
            >
              <option value="">Ledger account</option>
              {ledgerAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.code} · {account.name}
                </option>
              ))}
            </select>
            <Button type="button" size="sm" onClick={handlePlaidConnect} disabled={plaidConnecting || !plaidLedgerAccountId || ledgerLoading}>
              {plaidConnecting ? "Launching…" : "Connect with Plaid"}
            </Button>
          </section>
          <form onSubmit={handleAddRule} className="space-y-2 text-xs">
            <p className="font-semibold text-foreground/70">Auto-match rule</p>
            <div className="grid gap-2 md:grid-cols-3">
              <input
                className="rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
                placeholder="Pattern"
                value={newRule.pattern}
                onChange={(event) => setNewRule((prev) => ({ ...prev, pattern: event.target.value }))}
              />
              <select
                className="rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
                value={newRule.account_id}
                onChange={(event) => setNewRule((prev) => ({ ...prev, account_id: Number(event.target.value) }))}
              >
                {ledgerAccounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.code} · {account.name}
                  </option>
                ))}
              </select>
              <input
                className="rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
                placeholder="Memo template"
                value={newRule.memo_template}
                onChange={(event) => setNewRule((prev) => ({ ...prev, memo_template: event.target.value }))}
              />
            </div>
            <Button type="submit" size="sm" disabled={savingRule || ledgerLoading}>
              {savingRule ? "Saving…" : "Save rule"}
            </Button>
          </form>
          <form onSubmit={handleImport} className="space-y-2 text-xs">
            <p className="font-semibold text-foreground/70">Import statement</p>
            <input type="file" accept=".csv" onChange={(event) => setUploadFile(event.target.files?.[0] ?? null)} />
            <Button type="submit" size="sm" disabled={importing || !selectedAccountId}>
              {importing ? "Importing…" : "Upload CSV"}
            </Button>
          </form>
          <div className="space-y-2 text-xs">
            <p className="font-semibold text-foreground/70">Connected feeds</p>
            {plaidBankAccounts.length === 0 ? (
              <p className="text-muted-foreground">No Plaid-connected bank feeds yet.</p>
            ) : (
              <div className="space-y-2">
                {plaidBankAccounts.map((account) => (
                  <div key={account.id} className="rounded-xl border border-border bg-background/50 p-3">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-primary-foreground dark:text-amber-100">{account.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {account.connection_provider_name ?? account.institution ?? "Plaid"} &middot; {formatLastSynced(account.connection_last_synced_at)}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handlePlaidSync(account.id)}
                        disabled={syncingAccountId === account.id}
                      >
                        {syncingAccountId === account.id ? "Syncing…" : "Sync now"}
                      </Button>
                    </div>
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                      {account.connection_provider_status ?? "connected"}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>

      {activeTxn && (
        <div className="space-y-6 rounded-2xl border border-border bg-background/70 p-5">
          <header className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-primary-foreground dark:text-amber-200">Reconcile transaction</h3>
              <p className="text-xs text-muted-foreground">Offset lines or match to an existing entry.</p>
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              <Button size="sm" variant="outline" onClick={() => setSplitMode(false)} className={!splitMode ? "border-primary text-primary" : undefined}>
                Direct offset
              </Button>
              <Button size="sm" variant="outline" onClick={() => setSplitMode(true)} className={splitMode ? "border-primary text-primary" : undefined}>
                Split
              </Button>
            </div>
          </header>

          {!splitMode ? (
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Memo
                  <input
                    className="mt-1 w-full rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
                    value={memo}
                    onChange={(event) => setMemo(event.target.value)}
                  />
                </label>
              </div>
              <div className="space-y-3">
                {offsetLines.map((line, index) => (
                  <div key={`${line.accountId}-${index}`} className="grid gap-3 rounded-xl border border-border bg-background/30 p-3 text-sm md:grid-cols-4">
                    <select
                      className="rounded-lg border border-border bg-background/40 px-3 py-2 text-foreground"
                      value={line.accountId}
                      onChange={(event) => handleUpdateLine(index, { accountId: Number(event.target.value) })}
                    >
                      <option value="">Ledger account</option>
                      {ledgerAccounts.map((account) => (
                        <option key={account.id} value={account.id}>
                          {account.code} · {account.name}
                        </option>
                      ))}
                    </select>
                    <input
                      className="rounded-lg border border-border bg-background/40 px-3 py-2 text-foreground"
                      placeholder="Debit"
                      value={line.debit}
                      onChange={(event) => handleUpdateLine(index, { debit: event.target.value, credit: "" })}
                    />
                    <input
                      className="rounded-lg border border-border bg-background/40 px-3 py-2 text-foreground"
                      placeholder="Credit"
                      value={line.credit}
                      onChange={(event) => handleUpdateLine(index, { credit: event.target.value, debit: "" })}
                    />
                    <input
                      className="rounded-lg border border-border bg-background/40 px-3 py-2 text-foreground"
                      placeholder="Memo"
                      value={line.memo}
                      onChange={(event) => handleUpdateLine(index, { memo: event.target.value })}
                    />
                    <div className="md:col-span-4">
                      <Button type="button" size="sm" variant="ghost" onClick={() => handleRemoveOffsetLine(index)} disabled={offsetLines.length <= 1}>
                        Remove line
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-3">
                <Button type="button" variant="outline" size="sm" onClick={handleAddOffsetLine}>
                  Add line
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={handleMatch}>
                  Match entry
                </Button>
                <input
                  type="text"
                  placeholder="Entry ID"
                  value={matchEntryId}
                  onChange={(event) => setMatchEntryId(event.target.value)}
                  className="rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
                />
              </div>
              <div className="flex flex-wrap gap-3">
                <select
                  className="rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
                  value={selectedJournalId ?? ""}
                  onChange={(event) => setSelectedJournalId(Number(event.target.value))}
                >
                  <option value="" disabled>
                    Select journal
                  </option>
                  {journals.map((journal) => (
                    <option key={journal.id} value={journal.id}>
                      {journal.code} · {journal.name}
                    </option>
                  ))}
                </select>
                <Button onClick={handleOffset} disabled={Boolean(reconcilingId)}>
                  {reconcilingId ? "Reconciling…" : "Reconcile"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {splitSegments.map((segment, segmentIndex) => (
                <div key={`segment-${segmentIndex}`} className="space-y-3 rounded-xl border border-border bg-background/30 p-4">
                  <div className="grid gap-3 md:grid-cols-3">
                    <input
                      className="rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
                      placeholder="Amount"
                      value={segment.amount}
                      onChange={(event) => {
                        const amount = event.target.value;
                        setSplitSegments((prev) =>
                          prev.map((seg, idx) => (idx === segmentIndex ? { ...seg, amount } : seg)),
                        );
                      }}
                    />
                    <input
                      className="rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
                      placeholder="Memo"
                      value={segment.memo}
                      onChange={(event) => {
                        const memoValue = event.target.value;
                        setSplitSegments((prev) => prev.map((seg, idx) => (idx === segmentIndex ? { ...seg, memo: memoValue } : seg)));
                      }}
                    />
                    <div className="flex items-center justify-end gap-2">
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeSplitSegment(segmentIndex)}>
                        Remove split
                      </Button>
                    </div>
                  </div>
                  {segment.lines.map((line, lineIndex) => (
                    <div key={`line-${lineIndex}`} className="grid gap-2 md:grid-cols-4">
                      <select
                        className="rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
                        value={line.accountId}
                        onChange={(event) => handleUpdateSplitLine(segmentIndex, lineIndex, { accountId: Number(event.target.value) })}
                      >
                        <option value="">Ledger account</option>
                        {ledgerAccounts.map((account) => (
                          <option key={account.id} value={account.id}>
                            {account.code} · {account.name}
                          </option>
                        ))}
                      </select>
                      <input
                        className="rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
                        placeholder="Debit"
                        value={line.debit}
                        onChange={(event) => handleUpdateSplitLine(segmentIndex, lineIndex, { debit: event.target.value, credit: "" })}
                      />
                      <input
                        className="rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
                        placeholder="Credit"
                        value={line.credit}
                        onChange={(event) => handleUpdateSplitLine(segmentIndex, lineIndex, { credit: event.target.value, debit: "" })}
                      />
                      <input
                        className="rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
                        placeholder="Memo"
                        value={line.memo}
                        onChange={(event) => handleUpdateSplitLine(segmentIndex, lineIndex, { memo: event.target.value })}
                      />
                      <div className="md:col-span-4">
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeSplitLine(segmentIndex, lineIndex)}>
                          Remove line
                        </Button>
                      </div>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={() => addSplitLine(segmentIndex)}>
                    Add line
                  </Button>
                </div>
              ))}
              <div className="flex flex-wrap gap-3">
                <Button type="button" variant="outline" size="sm" onClick={addSplitSegment}>
                  Add split segment
                </Button>
                <Button onClick={handleSplit} disabled={Boolean(reconcilingId)}>
                  {reconcilingId ? "Posting…" : "Post split"}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
      <MappingHealthModal
        open={healthModalOpen}
        health={mergedHealth}
        ctaHref="/accounting"
        onClose={() => {
          setHealthModalOpen(false);
          if (!externalHealth) {
            void refreshHealth();
          }
        }}
      />
    </div>
  );
}
